// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { Handler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { CatalogPage } from "@insite/client-framework/Services/CategoryService";
import {
    GetProductCollectionApiV2Parameter,
    ProductExpandTokens,
} from "@insite/client-framework/Services/ProductServiceV2";
import { getSearchDataModeActive } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import displayProducts, {
    chain,
    RemoveNotAvailableAttributeValues,
    SetExpandTokens,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { ProductFilters } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import qs from "qs";

interface Parameter {
    queryString: string;
    path: string;
    isSearch: boolean;
}

export interface DisplayProductsResult {
    products?: ProductModel[];
    productInfosByProductId: SafeDictionary<ProductInfo>;
    productFilters: ProductFilters;
    isFiltered: boolean;
    unfilteredApiParameter?: GetProductCollectionApiV2Parameter;
    filteredApiParameter?: GetProductCollectionApiV2Parameter;
    catalogPage?: CatalogPage;
}

interface Props {
    apiParameter: GetProductCollectionApiV2Parameter;
    idByPath?: SafeDictionary<string>;
    result: DisplayProductsResult;
    pricingLoaded?: true;
    expandTokens?: ProductExpandTokens;
}

type HandlerType = Handler<Parameter, Props>;

export const XngSetExpandTokens: typeof SetExpandTokens = props => {
    props.expandTokens = ["attributes", "facets", "variantTraits", "badges", "properties"];

    if (getSearchDataModeActive(props.getState())) {
        props.expandTokens.push("scoreexplanation");
    }
};

export const XngRemoveNotAvailableAttributeValues: typeof RemoveNotAvailableAttributeValues = props => {
    const productsDataView = getProductsDataView(props.getState(), props.apiParameter);
    if (!productsDataView.value) {
        return;
    }

    const possibleAttributeValueIds = new Set();
    productsDataView?.attributeTypeFacets?.forEach(a =>
        a.attributeValueFacets?.forEach(v => possibleAttributeValueIds.add(v.attributeValueId)),
    );
    const removedAttributeValueIds = props.result.productFilters.attributeValueIds?.filter(
        o => !possibleAttributeValueIds.has(o),
    );
    // <xng-change> Issue: https://xngage.atlassian.net/browse/SAINC-913 This change from the last upgrade to remove attributes
    // from filters if those attributes are not present on the filtered data
    // In case there is no results, dont call for the api again
    if (removedAttributeValueIds?.length && productsDataView?.attributeTypeFacets?.length) {
        const attributeValueIds = props.result.productFilters.attributeValueIds?.filter(o =>
            possibleAttributeValueIds.has(o),
        );
        props.dispatch(removeProductFilters({ attributeValueIds: removedAttributeValueIds }));
        const parsedQuery = parseQueryString<any>((props.parameter as any).queryString || "");
        parsedQuery.attributeValueIds = attributeValueIds?.join(",");
        const parameter = { ...props.parameter, queryString: qs.stringify(parsedQuery) };
        props.dispatch(displayProducts(parameter));
        return false;
    }
};

replaceInChain(chain, SetExpandTokens, XngSetExpandTokens);
replaceInChain(chain, RemoveNotAvailableAttributeValues, XngRemoveNotAvailableAttributeValues);
